import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Row, Col, Popover } from 'antd';

import dynamic from 'next/dynamic';
import * as styles from './index.module.scss';

import useExplorerStats, { useStatsCharts } from '../../../hooks';
import useIsMobile from '../../../hooks/useIsMobile';

const Chart = dynamic(() => import('react-apexcharts'), { ssr: false });

interface ItemProps {
  imgUrl: string;
  label: string;
  value: string;
  popover?: React.JSX.Element;
}
const Item = ({ imgUrl, label, value, popover }: ItemProps) => {
  const isMobile = useIsMobile();
  return (
    <div className={styles.bottomContent}>
      <img src={imgUrl} alt="" />
      <div className={styles.content}>
        <div className={styles.label}>
          {label}
          {popover && isMobile && (
            <Popover
              content={popover}
              overlayClassName={styles.overlaywrap}
              overlayInnerStyle={{
                color: '#fff',
                background: '#020722',
              }}
              color="#020722"
            >
              <img
                className={styles.popoverImg1}
                src="https://cdn-web.mtt.xyz/static/circle-information.png"
                alt=""
              />
            </Popover>
          )}
        </div>
        <div className={styles.title}>{value}</div>
        {popover && !isMobile && (
          <Popover
            content={popover}
            overlayClassName={styles.overlaywrap}
            overlayInnerStyle={{
              color: '#fff',
              background: '#020722',
            }}
            color="#020722"
          >
            <img
              className={styles.popoverImg}
              src="https://cdn-web.mtt.xyz/static/circle-information.png"
              alt=""
            />
          </Popover>
        )}
      </div>
    </div>
  );
};
const DataArea = () => {
  const { stats } = useExplorerStats();
  const { data: tableData } = useStatsCharts();

  const items = [
    {
      imgUrl: 'https://cdn-web.mtt.xyz/static/Frame%201410077814.png',
      label: 'Total blocks',
      value: Number(stats?.total_blocks).toLocaleString(),
    },
    {
      imgUrl: 'https://cdn-web.mtt.xyz/static/Frame%201410077814%20(1).png',
      label: 'Average block time',
      value: (stats?.average_block_time || 0) / 1000 + ' s',
    },
    {
      imgUrl: 'https://cdn-web.mtt.xyz/static/Frame%201410077814%20(2).png',
      label: 'Total transactions',
      value: Number(stats?.total_transactions).toLocaleString(),
    },
    {
      imgUrl: 'https://cdn-web.mtt.xyz/static/Frame%201410077814%20(3).png',
      label: 'Wallet addresses',
      value: Number(stats?.total_addresses).toLocaleString(),
    },
    {
      imgUrl: 'https://cdn-web.mtt.xyz/static/Frame%201410077814%20(4).png',
      label: 'Gas tracker',
      value: Number(stats?.gas_prices.average.price).toLocaleString() + ' Gwei',
      popover: (
        <div className={styles.tipsWrap}>
          <div className={styles.tipsTitle}>
            <span>Last update</span>
            <span>
              {dayjs(stats?.gas_price_updated_at).format('MMM DD, HH:mm:ss')}
            </span>
          </div>
          <div className={styles.tipsLine}>
            <span>
              Fast{' '}
              <span>
                {((stats?.gas_prices?.fast?.time || 0) / 1000).toFixed(2)}s
              </span>
            </span>
            <span>
              {Number(stats?.gas_prices.fast.price).toLocaleString() + ' Gwei'}
            </span>
          </div>
          <div className={styles.tipsLine}>
            <span>
              Normal{' '}
              <span>
                {((stats?.gas_prices?.average?.time || 0) / 1000).toFixed(2)}s
              </span>
            </span>
            <span>
              {Number(stats?.gas_prices.average.price).toLocaleString() +
                ' Gwei'}
            </span>
          </div>
          <div className={styles.tipsLine}>
            <span>
              Slow{' '}
              <span>
                {((stats?.gas_prices?.slow?.time || 0) / 1000).toFixed(2)}s
              </span>
            </span>
            <span>
              {Number(stats?.gas_prices.slow.price).toLocaleString() + ' Gwei'}
            </span>
          </div>
          <a href="https://explorer.mtt.network/gas-tracker">
            Gas tracker overview
          </a>
        </div>
      ),
    },
  ];

  var options = {
    series: [
      {
        data: tableData?.map?.((item) => item.transaction_count || 0) || [],
      },
    ],
    tooltip: {
      cssClass: styles.tableTooltips,
      theme: 'dark',
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        if (!tableData) return ""
        const date = tableData?.[dataPointIndex].date || 0;
        const txCount = tableData?.[dataPointIndex].transaction_count || 0;
        return `<div class="content">
                  <span><i>Date</i><label>${date}</label></span>
                  <span><i>Tx/day</i><label>${txCount}</label></span>
                </div>`;
      },
    },
    chart: {
      type: 'area',
      height: 160,
      sparkline: {
        enabled: true,
      },
    },
    stroke: {
      width: 2,
      curve: 'straight',
      colors: ['#AEC5FF'],
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        type: 'vertical',
        opacityFrom: 1,
        opacityTo: 0,
        stops: [-15.17, 100],
        colorStops: [
          {
            offset: 0,
            color: '#AEC5FF',
            opacity: 1,
          },
          {
            offset: 100,
            color: 'rgba(25, 48, 121, 0.00)',
            opacity: 0,
          },
        ],
      },
    },
    xaxis: {
      crosshairs: {
        width: 1,
      },
    },
    yaxis: {
      labels: {
        show: false, // 隐藏 Y 轴刻度
      },
      min: 0,
    },
  };

  return (
    <div className={styles.dataArea}>
      <Row
        className={styles.bottom}
        justify={{
          xxl: 'space-between',
          xl: 'space-between',
          lg: 'space-between',
          md: 'space-between',
          sm: 'center',
          xs: 'center',
        }}
      >
        <div className={styles.leftContent}>
          {items.map(({ imgUrl, label, value, popover }, index) => {
            return (
              <Item
                key={label + index}
                imgUrl={imgUrl}
                label={label}
                value={value}
                popover={popover}
              />
            );
          })}
        </div>
        <div className={styles.rightContent}>
          <div className={styles.topSection}>
            <img
              src="https://cdn-web.mtt.xyz/static/Frame%201410077814%20(5).png"
              alt=""
            />
            <div className={styles.right}>
              <div className={styles.label}>
                Daily transactions{' '}
                <Popover
                  content="Number of transactions yesterday (0:00 -23:59 UTC). The chart displays dailytransactions for the past 30 days."
                  overlayClassName={styles.overlaywrap}
                  overlayInnerStyle={{
                    color: '#fff',
                    maxWidth: '406px',
                    background: '#020722',
                  }}
                  color="#020722"
                >
                  <img
                    className={styles.popoverImg}
                    src="https://cdn-web.mtt.xyz/static/circle-information.png"
                    alt=""
                  />
                </Popover>
              </div>
              <div className={styles.title}>
                {Number(stats?.transactions_today) / 1000}k
              </div>
            </div>
          </div>
          <Chart
            // options={options} series={series} type="area"
            // {...optionsSpark3}
            type="area"
            width="100%"
            height={162}
            series={options.series}
            options={options}
          />
        </div>
      </Row>
    </div>
  );
};

export default DataArea;
